import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plug, Check, X, RefreshCw, Trash2, Eye, EyeOff, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const INTEGRATIONS = [
  { id: 'datadog', name: 'Datadog', desc: 'Metrics, logs, and alert monitoring', category: 'Monitoring',
    fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'app_key', label: 'Application Key', type: 'password' }, { key: 'site', label: 'Site', type: 'text', placeholder: 'datadoghq.com' }],
    color: '#632CA6', webhook: '/api/webhooks/datadog' },
  { id: 'pagerduty', name: 'PagerDuty', desc: 'Incident management and on-call', category: 'Monitoring',
    fields: [{ key: 'api_key', label: 'API Token', type: 'password' }, { key: 'webhook_secret', label: 'Webhook Secret', type: 'password' }],
    color: '#06AC38', webhook: '/api/webhooks/pagerduty' },
  { id: 'aws', name: 'AWS (CloudWatch)', desc: 'AWS metrics, alarms, and resource discovery', category: 'Cloud',
    fields: [{ key: 'access_key_id', label: 'Access Key ID', type: 'password' }, { key: 'secret_access_key', label: 'Secret Access Key', type: 'password' }, { key: 'region', label: 'Region', type: 'text', placeholder: 'us-east-1' }],
    color: '#FF9900', webhook: '/api/webhooks/cloudwatch' },
  { id: 'prometheus', name: 'Prometheus', desc: 'Open-source metrics and alerting', category: 'Monitoring',
    fields: [{ key: 'url', label: 'Prometheus URL', type: 'text', placeholder: 'http://prometheus:9090' }, { key: 'auth_token', label: 'Auth Token (optional)', type: 'password' }],
    color: '#E6522C', webhook: '/api/webhooks/prometheus' },
  { id: 'kubernetes', name: 'Kubernetes', desc: 'Cluster discovery and workload management', category: 'Cloud',
    fields: [{ key: 'api_url', label: 'API Server URL', type: 'text', placeholder: 'https://k8s-api:6443' }, { key: 'token', label: 'Service Account Token', type: 'password' }],
    color: '#326CE5' },
  { id: 'slack', name: 'Slack', desc: 'HERALD notifications to Slack channels', category: 'Communication',
    fields: [{ key: 'webhook_url', label: 'Webhook URL', type: 'text', placeholder: 'https://hooks.slack.com/services/...' }, { key: 'bot_token', label: 'Bot Token', type: 'password' }],
    color: '#4A154B' },
  { id: 'teams', name: 'Microsoft Teams', desc: 'HERALD notifications to Teams channels', category: 'Communication',
    fields: [{ key: 'webhook_url', label: 'Webhook URL', type: 'text', placeholder: 'https://outlook.office.com/webhook/...' }],
    color: '#464EB8' },
  // ITSM / RMM Platforms
  { id: 'kaseya', name: 'Kaseya VSA', desc: 'RMM alerts, patch status, device monitoring from Kaseya VSA/BMS', category: 'ITSM Platform',
    fields: [{ key: 'api_url', label: 'VSA API URL', type: 'text', placeholder: 'https://your-vsa.kaseya.net/api/v1.0' }, { key: 'api_key', label: 'API Key', type: 'password' }, { key: 'webhook_secret', label: 'Webhook Secret', type: 'password' }],
    color: '#00A4E4', webhook: '/api/webhooks/kaseya' },
  { id: 'solarwinds', name: 'SolarWinds Orion', desc: 'Network/server monitoring, alerts, and node discovery from SolarWinds', category: 'ITSM Platform',
    fields: [{ key: 'orion_url', label: 'Orion Server URL', type: 'text', placeholder: 'https://orion.company.com:17778' }, { key: 'username', label: 'API Username', type: 'text' }, { key: 'password', label: 'API Password', type: 'password' }],
    color: '#FC6D26', webhook: '/api/webhooks/solarwinds' },
];

export default function IntegrationsPage() {
  const token = localStorage.getItem('nexus_token');
  const queryClient = useQueryClient();
  const [configuring, setConfiguring] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [showSecrets, setShowSecrets] = useState({});

  const { data: statuses } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/integrations`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, credentials }) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/integrations/${id}`, { credentials }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: (data) => { toast.success(`${data.integration} configured`); setConfiguring(null); setFormValues({}); queryClient.invalidateQueries(['integrations']); },
    onError: () => toast.error('Failed to save credentials'),
  });

  const validateMutation = useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/integrations/${id}/validate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: (data) => toast[data.valid ? 'success' : 'error'](data.message || (data.valid ? 'Connection valid' : 'Validation failed')),
  });

  const removeMutation = useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.delete(`${BACKEND_URL}/api/integrations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: () => { toast.success('Integration removed'); queryClient.invalidateQueries(['integrations']); },
  });

  const categories = [...new Set(INTEGRATIONS.map(i => i.category))];

  return (
    <div data-testid="integrations-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Integrations</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Connect your monitoring tools, cloud providers, and communication channels</p>
      </div>

      {categories.map(category => (
        <div key={category} className="mb-6">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-secondary)] uppercase tracking-wider mb-3">{category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {INTEGRATIONS.filter(i => i.category === category).map(integration => {
              const isConfigured = statuses?.[integration.id]?.configured;
              const isConfiguring = configuring === integration.id;

              return (
                <div key={integration.id} className="nexus-card relative overflow-hidden" data-testid={`integration-${integration.id}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${integration.color}20` }}>
                          <Plug size={14} style={{ color: integration.color }} />
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-[var(--nexus-text-primary)]">{integration.name}</span>
                          <p className="text-[10px] text-[var(--nexus-text-muted)]">{integration.desc}</p>
                        </div>
                      </div>
                      {isConfigured ? (
                        <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-300 border border-green-500/20">
                          <Check size={10} /> Active
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)]">
                          Not configured
                        </span>
                      )}
                    </div>

                    {/* Configuration form */}
                    {isConfiguring ? (
                      <div className="space-y-2 animate-nexus-slide-in">
                        {integration.fields.map(field => (
                          <div key={field.key}>
                            <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">{field.label}</label>
                            <div className="flex items-center gap-1 mt-0.5">
                              <input
                                type={showSecrets[field.key] ? 'text' : (field.type || 'text')}
                                value={formValues[field.key] || ''}
                                onChange={e => setFormValues(v => ({ ...v, [field.key]: e.target.value }))}
                                placeholder={field.placeholder || ''}
                                className="flex-1 px-2 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)]"
                              />
                              {field.type === 'password' && (
                                <button onClick={() => setShowSecrets(s => ({ ...s, [field.key]: !s[field.key] }))} className="p-1 text-[var(--nexus-text-muted)]">
                                  {showSecrets[field.key] ? <EyeOff size={12} /> : <Eye size={12} />}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className="flex items-center gap-2 pt-2">
                          <button onClick={() => saveMutation.mutate({ id: integration.id, credentials: formValues })}
                            className="flex-1 px-3 py-1.5 rounded-lg bg-[var(--nexus-accent)] text-white text-[10px] font-medium hover:bg-opacity-90">
                            Save & Connect
                          </button>
                          <button onClick={() => { setConfiguring(null); setFormValues({}); }}
                            className="px-3 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)] text-[10px] border border-[var(--nexus-border)]">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setConfiguring(integration.id); setFormValues({}); }}
                          className="flex-1 px-3 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] hover:bg-white/[0.04]"
                          data-testid={`configure-${integration.id}`}>
                          {isConfigured ? 'Reconfigure' : 'Configure'}
                        </button>
                        {isConfigured && (
                          <>
                            <button onClick={() => validateMutation.mutate(integration.id)}
                              className="p-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[var(--nexus-text-muted)] hover:text-[var(--nexus-success)]">
                              <RefreshCw size={12} className={validateMutation.isPending ? 'animate-spin' : ''} />
                            </button>
                            <button onClick={() => removeMutation.mutate(integration.id)}
                              className="p-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[var(--nexus-text-muted)] hover:text-[var(--nexus-danger)]">
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Webhook URL */}
                    {integration.webhook && (
                      <div className="mt-2 px-2 py-1 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                        <span className="text-[9px] text-[var(--nexus-text-muted)]">Webhook: </span>
                        <code className="text-[9px] font-mono text-[var(--nexus-accent)]">{BACKEND_URL}{integration.webhook}</code>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Security note */}
      <div className="nexus-card p-4 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={14} className="text-[var(--nexus-success)]" />
          <span className="text-sm font-semibold text-[var(--nexus-text-primary)]">Credential Security</span>
        </div>
        <p className="text-[11px] text-[var(--nexus-text-muted)] leading-relaxed">
          All API keys and secrets are encrypted at rest using AES-256 (Fernet symmetric encryption). Credentials are never exposed in API responses.
          When agents need to access external services, credentials are decrypted in-memory for the duration of the API call only.
          All credential operations are logged to the immutable audit trail.
        </p>
      </div>
    </div>
  );
}
